import { Fragment, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { toastr } from "react-redux-toastr";
import { Spinner } from "../../../components/Spinner";
import {
  useDatosTramiteMutation,
  useGetPolizaQuery,
} from "../../../services/docs";
import { useForm } from "../../../hooks/useForm";

export const NuevoTramitePoliza = () => {
  const { id } = useParams();
  const navigate = useNavigate();

  // Queries y Mutations
  const { data: poliza, isLoading: cargandoPoliza } = useGetPolizaQuery(id);
  const [datosTramite, { isLoading: isLoadingDatos }] = useDatosTramiteMutation();

  // Estados locales
  const [cobertura_seleccionada, set_cobertura_seleccionada] = useState<any>(null);
  const [siniestroGenerado, setSiniestroGenerado] = useState(false);
  const [seleccionSiniestro, setSeleccionSiniestro] = useState<string | null>(null);
  const [seleccionTramite, setSeleccionTramite] = useState<string | null>(null);

  // Formulario
  const {
    formulario: dos,
    handleInputChange: hcDos,
    handleChangeAll: hcaDos,
  } = useForm({
    fi_cobertura_id: 0,
    fi_poliza_id: 0,
    fi_numero_asegurado: 0,
    fc_clave_asegurado: "",
    fecha_siniestro: "",
    siniestro: "",
    montoReclamar: "0.01",
    nombreContacto: "",
    telefonoContacto: "",
    correoContacto: "",
    descripcion: "",
    fc_poliza_aseguradora: "",
  });

  // Cálculo del paso actual para la UI
  const pasoActual = siniestroGenerado ? 4 : cobertura_seleccionada ? 3 : 2;

  // Acciones
  const iniciarTramite = (p: any, a: any, c: any) => {
    set_cobertura_seleccionada({
      ...c,
      tcc_fc_descripcion_cobertura:
        c.ttcp_fi_cobertura_adicional === 0
          ? c.tcc_fc_descripcion_cobertura
          : c.fc_nombre,
      tca_fc_nombre_asegurado: a.tca_fc_nombre_asegurado,
      tca_fc_email: a.tca_fc_email,
    });

    hcaDos({
      fi_cobertura_id: c.tcc_id,
      fi_numero_asegurado: a.tca_fi_numero_asegurado,
      fc_clave_asegurado: a.tca_fc_clave_asegurado,
      fc_poliza: a.ttap_fc_numero_poliza,
      fecha_siniestro: "",
      fc_poliza_aseguradora: a.fc_poliza_aseguradora,
      fi_poliza_id: p.ttp_id,
      fi_id_asegurado: a.tca_id,
      siniestro: "",
      montoReclamar: "0.01",
      nombreContacto: "",
      telefonoContacto: "",
      correoContacto: "",
      descripcion: "",
    });
  };

  const generarTramite = async () => {
    if (!dos.fecha_siniestro) {
      toastr.error("Debe seleccionar la fecha del siniestro");
      return;
    }

    // Formatear fecha
    let fecha_buena = dos.fecha_siniestro.split("-");
    let fecha_siniestro = fecha_buena[2] + "-" + fecha_buena[1] + "-" + fecha_buena[0];

    try {
      const tramite = await datosTramite({
        ...dos,
        fecha_siniestro,
      }).unwrap();

      if (tramite) {
        setSiniestroGenerado(true);
        setSeleccionSiniestro(tramite.siniestro);
        setSeleccionTramite(tramite.tramite_id);
        toastr.success("Siniestro generado correctamente", tramite.siniestro);
      }
    } catch (err: any) {
      if (err?.status === 400 && err?.data?.message) {
        if (Array.isArray(err.data.message)) {
          err.data.message.forEach((m: string) => toastr.error(m));
        } else {
          toastr.error(err.data.message);
        }
      }
    }
  };

  const verMas = () => {
    if (seleccionSiniestro && seleccionTramite) {
      navigate(`/view/siniestro/${seleccionSiniestro}/${seleccionTramite}/proceso`);
    }
  };

  // Función auxiliar para renderizar los steps dinámicamente
  const getStepClass = (step: number) => {
    if (step < pasoActual) return "bg-emerald-50 border-emerald-200 text-emerald-700"; // Completado
    if (step === pasoActual) return "bg-brand-50/80 border-brand-200/70 text-brand-600"; // Activo
    return "border-transparent text-slate-400"; // Pendiente
  };
  const getStepCircle = (step: number) => {
    if (step < pasoActual) return "bg-emerald-500 text-white";
    if (step === pasoActual) return "bg-brand-500 text-white";
    return "bg-slate-100 text-slate-400";
  };

  return (
    <div className="space-y-6">
      {/* --- Stepper Nav --- */}
      <div className="rounded-2xl border border-slate-200/80 bg-white p-4 shadow-sm">
        <div className="grid grid-cols-1 gap-4 md:grid-cols-4">
          <div className="flex items-center gap-3 rounded-xl border border-emerald-200 bg-emerald-50 px-4 py-2.5">
            <span className="flex h-7 w-7 items-center justify-center rounded-lg bg-emerald-500 text-xs font-bold text-white">✓</span>
            <div>
              <p className="text-xs font-semibold uppercase tracking-wider text-emerald-600">Paso 1</p>
              <p className="text-sm font-bold text-emerald-900">Buscar Póliza</p>
            </div>
          </div>
          <div className={`flex items-center gap-3 rounded-xl border px-4 py-2.5 ${getStepClass(2)}`}>
            <span className={`flex h-7 w-7 items-center justify-center rounded-lg text-xs font-bold ${getStepCircle(2)}`}>{pasoActual > 2 ? '✓' : '2'}</span>
            <div>
              <p className="text-xs font-semibold uppercase tracking-wider opacity-80">Paso 2</p>
              <p className="text-sm font-bold">Datos Asegurado</p>
            </div>
          </div>
          <div className={`flex items-center gap-3 rounded-xl border px-4 py-2.5 ${getStepClass(3)}`}>
            <span className={`flex h-7 w-7 items-center justify-center rounded-lg text-xs font-bold ${getStepCircle(3)}`}>{pasoActual > 3 ? '✓' : '3'}</span>
            <div>
              <p className="text-xs font-semibold uppercase tracking-wider opacity-80">Paso 3</p>
              <p className="text-sm font-bold">Detalle Siniestro</p>
            </div>
          </div>
          <div className={`flex items-center gap-3 rounded-xl border px-4 py-2.5 ${getStepClass(4)}`}>
            <span className={`flex h-7 w-7 items-center justify-center rounded-lg text-xs font-bold ${getStepCircle(4)}`}>4</span>
            <div>
              <p className="text-xs font-semibold uppercase tracking-wider opacity-80">Paso 4</p>
              <p className="text-sm font-bold">Resolución y Folio</p>
            </div>
          </div>
        </div>
      </div>

      {cargandoPoliza && (
        <div className="flex w-full justify-center py-10">
          <Spinner />
        </div>
      )}

      {/* --- PASO 2: Selección de Póliza y Cobertura --- */}
      {pasoActual === 2 && poliza?.poliza && poliza.poliza.length > 0 && (
        <div className="grid gap-6 md:grid-cols-1 lg:grid-cols-2">
          {poliza.poliza.map((p: any) => (
            <div key={p.ttp_id} className="overflow-hidden rounded-2xl border border-slate-200/80 bg-white shadow-sm">
              <div className="border-b border-slate-100 bg-slate-50/50 px-6 py-4">
                <h3 className="text-base font-bold text-slate-900">
                  {p.ttp_fi_numero_producto} - {p.tcp_fc_descripcion_producto}
                </h3>
                <p className="text-xs font-medium text-slate-500">
                  {p.tcp_fc_descripcion_aseguradora} • Póliza Interna: <span className="font-bold text-brand-600">{p.ttp_fc_numero_poliza}</span>
                </p>
                <div className="mt-2 flex items-center gap-2">
                  <span className="inline-flex rounded-md bg-brand-50 px-2 py-1 text-xs font-semibold text-brand-600 ring-1 ring-inset ring-brand-500/20">
                    {p.ttp_fc_estatus_poliza}
                  </span>
                  {p.ttp_fc_url_poliza && (
                    <a target="_blank" rel="noreferrer" href={p.ttp_fc_url_poliza} className="text-xs font-semibold text-blue-500 hover:underline">
                      [ Descargar PDF Póliza ]
                    </a>
                  )}
                </div>
              </div>

              <div className="p-6">
                <h4 className="mb-4 text-sm font-bold uppercase tracking-wider text-slate-500">Asegurados Registrados</h4>
                <div className="space-y-6">
                  {p.asegurados.map((a: any) => (
                    <Fragment key={a.ttap_id}>
                      <div className="rounded-xl border border-slate-100 p-4">
                        <div className="mb-3 flex items-center justify-between border-b border-slate-100 pb-3">
                          <div>
                            <div className="font-bold text-slate-900">{a.tca_fc_nombre_asegurado}</div>
                            <div className="text-xs text-slate-500">RFC: {a.tca_fc_rfc} • Correo: {a.tca_fc_email}</div>
                          </div>
                          <span className="rounded-full bg-slate-100 px-3 py-1 text-xs font-semibold text-slate-600">
                            {a.fc_descripcion_tipo_asegurado}
                          </span>
                        </div>

                        {/* Coberturas del Asegurado */}
                        {a.ttap_fi_tipo_asegurado !== 9 && a.coberturas.length > 0 && (
                          <div className="space-y-2">
                            <h5 className="text-xs font-semibold text-slate-400">Coberturas Disponibles:</h5>
                            {a.coberturas.map((c: any) => {
                              const puedeIniciar = ((c.ttcp_fi_cobertura_adicional === 1 && c.ttcp_fn_valor_is_cobertura > 0) || (c.tcc_fi_protec === 1 && c.ttcp_fi_cobertura_adicional === 0));
                              return (
                                <div key={c.tcc_id} className="flex flex-col justify-between rounded-lg bg-slate-50 px-3 py-2 sm:flex-row sm:items-center">
                                  <div className="mb-2 sm:mb-0">
                                    <div className="text-sm font-semibold text-slate-700">
                                      {c.ttcp_fi_numero_cobertura} - {c.ttcp_fi_cobertura_adicional === 0 ? c.tcc_fc_descripcion_cobertura : c.fc_nombre}
                                    </div>
                                    <div className="mt-1 flex items-center gap-2 text-xs">
                                      <span className="rounded bg-white px-1.5 py-0.5 border border-slate-200 text-slate-500">
                                        {c.ttcp_fi_cobertura_adicional === 0 ? c.tcc_fc_ramo : "Adicional"}
                                      </span>
                                      <span className="rounded bg-white px-1.5 py-0.5 border border-slate-200 font-mono text-slate-600">
                                        Valor: {c.ttcp_fn_valor_is_cobertura}
                                      </span>
                                    </div>
                                  </div>

                                  {puedeIniciar && (
                                    <button
                                      onClick={() => iniciarTramite(p, a, c)}
                                      className="inline-flex items-center gap-1.5 rounded-xl bg-slate-800 px-4 py-2 text-xs font-semibold text-white shadow-sm transition-colors hover:bg-slate-700"
                                    >
                                      Reclamar
                                      <svg className="h-3.5 w-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 5l7 7-7 7"></path></svg>
                                    </button>
                                  )}
                                </div>
                              );
                            })}
                          </div>
                        )}
                      </div>
                    </Fragment>
                  ))}
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* --- PASO 3: Formulario Detalle de Siniestro --- */}
      {pasoActual === 3 && cobertura_seleccionada && (
        <section className="overflow-hidden rounded-2xl border border-slate-200/80 bg-white shadow-sm">
          {/* Header de Resumen */}
          <div className="flex flex-col gap-4 border-b border-slate-100 bg-brand-900 px-7 py-5 text-white sm:flex-row sm:items-center sm:justify-between">
            <div>
              <p className="text-xs font-medium text-brand-200 uppercase tracking-wider">Cobertura a Reclamar</p>
              <h3 className="text-lg font-bold">
                {cobertura_seleccionada.tcc_id} - {cobertura_seleccionada.tcc_fc_descripcion_cobertura}
              </h3>
            </div>
            <div className="sm:text-right">
              <p className="text-xs font-medium text-brand-200 uppercase tracking-wider">Titular / Reclamante</p>
              <h3 className="text-base font-semibold">{cobertura_seleccionada.tca_fc_nombre_asegurado}</h3>
              <small className="text-sm ">{cobertura_seleccionada.tca_fc_email}</small>
            </div>
          </div>

          {/* Formulario */}
          <div className="px-7 py-6">
            <h2 className="mb-5 flex items-center gap-2 text-sm font-semibold text-slate-800">
              <svg className="h-4 w-4 text-brand-500" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"></path></svg>
              Detalles del Evento y Contacto
            </h2>

            <div className="grid grid-cols-1 gap-6 md:grid-cols-2">
              <div>
                <label className="mb-2 block text-xs font-bold uppercase tracking-wider text-slate-600">Fecha del Siniestro</label>
                <input
                  type="date"
                  name="fecha_siniestro"
                  onChange={hcDos}
                  value={dos.fecha_siniestro}
                  className="h-11 w-full rounded-xl border border-slate-200 bg-slate-50/50 px-3 text-sm font-medium text-slate-900 transition-all hover:bg-slate-50 focus:border-brand-500 focus:bg-white focus:ring-2 focus:ring-brand-500/20"
                />
              </div>
              <div>
                <label className="mb-2 block text-xs font-bold uppercase tracking-wider text-slate-600">Descripción breve del evento</label>
                <input
                  type="text"
                  name="descripcion"
                  placeholder="Ej. Choque por alcance, robo de vehículo..."
                  onChange={hcDos}
                  value={dos.descripcion}
                  className="h-11 w-full rounded-xl border border-slate-200 bg-slate-50/50 px-3 text-sm font-medium text-slate-900 transition-all hover:bg-slate-50 focus:border-brand-500 focus:bg-white focus:ring-2 focus:ring-brand-500/20"
                />
              </div>
              <div>
                <label className="mb-2 block text-xs font-bold uppercase tracking-wider text-slate-600">Nombre del Contacto</label>
                <input
                  type="text"
                  name="nombreContacto"
                  placeholder="Persona a contactar por este folio"
                  onChange={hcDos}
                  value={dos.nombreContacto}
                  className="h-11 w-full rounded-xl border border-slate-200 bg-slate-50/50 px-3 text-sm font-medium text-slate-900 transition-all hover:bg-slate-50 focus:border-brand-500 focus:bg-white focus:ring-2 focus:ring-brand-500/20"
                />
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="mb-2 block text-xs font-bold uppercase tracking-wider text-slate-600">Teléfono</label>
                  <input
                    type="tel"
                    name="telefonoContacto"
                    placeholder="10 dígitos"
                    onChange={hcDos}
                    value={dos.telefonoContacto}
                    className="h-11 w-full rounded-xl border border-slate-200 bg-slate-50/50 px-3 text-sm font-medium text-slate-900 transition-all focus:border-brand-500 focus:bg-white focus:ring-2 focus:ring-brand-500/20"
                  />
                </div>
                <div>
                  <label className="mb-2 block text-xs font-bold uppercase tracking-wider text-slate-600">Correo</label>
                  <input
                    type="email"
                    name="correoContacto"
                    placeholder="correo@ejemplo.com"
                    onChange={hcDos}
                    value={dos.correoContacto}
                    className="h-11 w-full rounded-xl border border-slate-200 bg-slate-50/50 px-3 text-sm font-medium text-slate-900 transition-all focus:border-brand-500 focus:bg-white focus:ring-2 focus:ring-brand-500/20"
                  />
                </div>
              </div>
            </div>

            <div className="mt-8 flex justify-end gap-3 border-t border-slate-100 pt-5">
              <button
                onClick={() => set_cobertura_seleccionada(null)}
                className="h-11 rounded-xl px-5 text-sm font-semibold text-slate-600 hover:bg-slate-100 transition-colors"
              >
                Cancelar y regresar
              </button>

              <button
                onClick={generarTramite}
                disabled={isLoadingDatos}
                className="flex h-11 items-center justify-center gap-2 rounded-xl bg-brand-500 px-6 text-sm font-semibold text-white shadow-sm shadow-brand-500/25 transition-all hover:-translate-y-0.5 hover:bg-brand-600 active:bg-brand-700 disabled:opacity-70"
              >
                {isLoadingDatos ? <Spinner /> : (
                  <>
                    <svg className="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 13l4 4L19 7"></path></svg>
                    Generar Folio de Siniestro
                  </>
                )}
              </button>
            </div>
          </div>
        </section>
      )}

      {/* --- PASO 4: Resolución (Success) --- */}
      {pasoActual === 4 && seleccionSiniestro && (
        <section className="overflow-hidden rounded-2xl border border-emerald-200 bg-white shadow-sm">
          <div className="bg-emerald-50 px-7 py-10 text-center">
            <div className="mx-auto mb-4 flex h-16 w-16 items-center justify-center rounded-full bg-emerald-100 text-emerald-600">
              <svg className="h-8 w-8" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2.5" d="M5 13l4 4L19 7"></path></svg>
            </div>
            <h2 className="mb-2 text-2xl font-bold text-emerald-900">Trámite Generado con Éxito</h2>
            <p className="text-emerald-700">El número de folio interno para este siniestro es:</p>
            <div className="my-6 text-3xl font-black tracking-widest text-slate-900">{seleccionSiniestro}</div>

            <p className="mb-8 text-sm text-slate-600">
              Ahora debe continuar a la etapa de carga de documentos e integración del expediente para iniciar el flujo de validación.
            </p>

            <button
              onClick={verMas}
              className="inline-flex h-12 items-center justify-center gap-2 rounded-xl bg-slate-900 px-8 text-sm font-semibold text-white shadow-sm transition-all hover:-translate-y-0.5 hover:bg-slate-800"
            >
              Continuar a Carga de Documentos
              <svg className="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2.5" d="M14 5l7 7m0 0l-7 7m7-7H3"></path></svg>
            </button>
          </div>
        </section>
      )}
    </div>
  );
};
